import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { normalizeApplicabilityValue } from '@carrierbridge/shared';
import api from '@/lib/api';

/**
 * Un moyen de transport du référentiel `means` de Connect4ward, tel que le
 * renvoie `GET /connect4ward/means`.
 *
 * `code` est ce que portent les expéditions et ce que restreignent les grilles ;
 * `name` n'est qu'un libellé d'affichage.
 */
export interface Means {
  code: string;
  name: string;
  category: string | null;
  active: boolean;
}

/**
 * Le référentiel ne sert qu'à nommer. Le lire est donc une commodité : une
 * erreur (pas de connexion Connect4ward, table non accordée) ne doit ni casser
 * l'écran ni être relancée à chaque rendu — on affiche alors les codes.
 */
export function useMeansCatalog() {
  return useQuery<Means[]>({
    queryKey: ['connect4ward', 'means'],
    queryFn: async () => {
      const { data } = await api.get<Means[]>('/connect4ward/means');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

/**
 * Code de moyen → nom lisible.
 *
 * La recherche passe par la forme canonique du code, comme le rapprochement des
 * grilles : « semi 44 » et « SEMI_44 » désignent le même moyen. Un code que le
 * référentiel ne connaît pas (ou pas encore chargé) est rendu tel quel : le
 * code reste exact, seul le confort de lecture manque.
 */
export function buildMeansNames(items: readonly Means[] | undefined): (code: string) => string {
  const byKey = new Map<string, string>();
  for (const m of items ?? []) {
    const key = normalizeApplicabilityValue(m.code);
    if (key && !byKey.has(key)) byKey.set(key, m.name);
  }
  return (code: string) => {
    const key = normalizeApplicabilityValue(code);
    return (key && byKey.get(key)) || code;
  };
}

/** `buildMeansNames` sur le référentiel chargé ; référence stable tant qu'il ne change pas. */
export function useMeansName(): (code: string) => string {
  const { data } = useMeansCatalog();
  return useMemo(() => buildMeansNames(data), [data]);
}
